import React, { useState, useEffect } from 'react';
import { getSalesReport, getLeadsReport, getBookingsReport, exportData } from '../lib/api';
import { formatCurrency } from '../lib/currency';
import { REPORTS } from '../constants/testIds';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  MapPin, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  File, 
  Loader2,
  BarChart3,
  PieChartIcon,
  Calendar,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#84bd0e', '#383838', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [salesData, setSalesData] = useState(null);
  const [leadsData, setLeadsData] = useState([]);
  const [bookingsData, setBookingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesRes, leadsRes, bookingsRes] = await Promise.all([
        getSalesReport(dateRange),
        getLeadsReport(),
        getBookingsReport(),
      ]);
      setSalesData(salesRes.data);
      setLeadsData(leadsRes.data);
      setBookingsData(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    setLoading(true);
    loadData();
  };

  const handleExportAll = async (format) => {
    setExporting(true);
    try {
      const modules = ['leads', 'customers', 'bookings', 'payments', 'invoices'];
      for (const module of modules) {
        const response = await exportData(module, format);
        const blob = new Blob([response.data], { 
          type: format === 'csv' ? 'text/csv' : 
                format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 
                'application/pdf'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${module}_report.${format === 'excel' ? 'xlsx' : format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(`All reports exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export reports');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Calculate additional metrics
  const totalLeads = leadsData.reduce((acc, l) => acc + l.count, 0);
  const wonLeads = leadsData.find(l => l.status === 'won')?.count || 0;
  const conversionRate = totalLeads > 0 ? ((wonLeads / totalLeads) * 100).toFixed(1) : 0;
  const totalBookings = bookingsData.reduce((acc, b) => acc + b.count, 0);
  const totalRevenue = salesData?.total_revenue || bookingsData.reduce((acc, b) => acc + (b.revenue || 0), 0);

  return (
    <div data-testid={REPORTS.container} className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Reports & Analytics</h1>
          <p className="text-muted-foreground mt-1">View business insights and performance metrics</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={exporting}>
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export All Reports
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Export Format</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportAll('csv')}>
              <FileText className="w-4 h-4 mr-2" />
              CSV (.csv)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportAll('excel')}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportAll('pdf')}>
              <File className="w-4 h-4 mr-2" />
              PDF (.pdf)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Date Filter */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              />
            </div>
            <Button onClick={handleDateFilter} className="bg-slate-900 hover:bg-slate-800">
              Apply Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900 font-outfit">{formatCurrency(totalRevenue)}</p>
                <p className="text-sm text-slate-500 mt-1">{salesData?.total_transactions || totalBookings} transactions</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-1">Total Leads</p>
                <p className="text-2xl font-bold text-slate-900 font-outfit">{totalLeads}</p>
                <p className="text-sm text-slate-500 mt-1">Across all statuses</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-1">Total Bookings</p>
                <p className="text-2xl font-bold text-slate-900 font-outfit">{totalBookings}</p>
                <p className="text-sm text-slate-500 mt-1">{bookingsData.length} destinations</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-1">Conversion Rate</p>
                <p className="text-2xl font-bold text-slate-900 font-outfit">{conversionRate}%</p>
                <p className="text-sm text-slate-500 mt-1">{wonLeads} won leads</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Different Reports */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="leads" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Users className="w-4 h-4 mr-2" />
            Leads Report
          </TabsTrigger>
          <TabsTrigger value="bookings" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Calendar className="w-4 h-4 mr-2" />
            Bookings Report
          </TabsTrigger>
          <TabsTrigger value="destinations" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <MapPin className="w-4 h-4 mr-2" />
            Destinations
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leads by Status Pie Chart */}
            <Card className="border-slate-200" data-testid={REPORTS.leadsChart}>
              <CardHeader>
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5" style={{ color: '#84bd0e' }} />
                  Leads by Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {leadsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={leadsData}
                          dataKey="count"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ status, count }) => `${status}: ${count}`}
                        >
                          {leadsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      No lead data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Bookings by Destination Bar Chart */}
            <Card className="border-slate-200" data-testid={REPORTS.salesChart}>
              <CardHeader>
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: '#383838' }} />
                  Bookings by Destination
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {bookingsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bookingsData.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis dataKey="destination" type="category" stroke="#64748b" fontSize={12} width={100} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'count' ? value : formatCurrency(value),
                            name === 'count' ? 'Bookings' : 'Revenue'
                          ]}
                        />
                        <Bar dataKey="count" fill="#84bd0e" name="count" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      No booking data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Monthly Revenue Trend */}
            {salesData?.monthly_data && salesData.monthly_data.length > 0 && (
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: '#383838' }} />
                    Monthly Revenue Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={salesData.monthly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                        <Area type="monotone" dataKey="revenue" stroke="#84bd0e" fill="#84bd0e" fillOpacity={0.2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Leads Report Tab */}
        <TabsContent value="leads" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Lead Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  {leadsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={leadsData}
                          dataKey="count"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          label
                        >
                          {leadsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      No lead data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Lead Status Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {leadsData.map((item, index) => (
                    <div key={item.status} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium capitalize">{item.status}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold">{item.count}</span>
                        <span className="text-sm text-slate-500">
                          ({totalLeads > 0 ? ((item.count / totalLeads) * 100).toFixed(1) : 0}%)
                        </span>
                      </div>
                    </div>
                  ))}
                  {leadsData.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No lead data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Bookings Report Tab */}
        <TabsContent value="bookings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Bookings by Package Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  {salesData?.by_package_type && salesData.by_package_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesData.by_package_type}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="package_type" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip formatter={(value, name) => [name === 'count' ? value : formatCurrency(value), name === 'count' ? 'Bookings' : 'Revenue']} />
                        <Bar dataKey="count" fill="#84bd0e" name="count" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      No package data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Package Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {salesData?.by_package_type?.map((item, index) => (
                    <div key={item.package_type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5" style={{ color: COLORS[index % COLORS.length] }} />
                        <div>
                          <span className="font-medium capitalize">{item.package_type || 'Standard'}</span>
                          <p className="text-sm text-slate-500">{item.count} bookings</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold" style={{ color: '#383838' }}>
                        {formatCurrency(item.revenue)}
                      </span>
                    </div>
                  ))}
                  {(!salesData?.by_package_type || salesData.by_package_type.length === 0) && (
                    <p className="text-center py-8 text-slate-500">No package data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Destinations Tab */}
        <TabsContent value="destinations" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-outfit text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: '#84bd0e' }} />
                Revenue by Destination
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bookingsData.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Rank</TableHead>
                      <TableHead className="font-semibold">Destination</TableHead>
                      <TableHead className="font-semibold text-center">Bookings</TableHead>
                      <TableHead className="font-semibold text-right">Revenue</TableHead>
                      <TableHead className="font-semibold text-right">Avg. Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookingsData
                      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
                      .map((item, index) => (
                        <TableRow key={item.destination} className="hover:bg-slate-50">
                          <TableCell>
                            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-sm">
                              {index + 1}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.destination}</TableCell>
                          <TableCell className="text-center">{item.count}</TableCell>
                          <TableCell className="text-right font-semibold" style={{ color: '#383838' }}>
                            {formatCurrency(item.revenue)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.count > 0 ? item.revenue / item.count : 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center py-8 text-slate-500">No booking data available</p>
              )}
            </CardContent>
          </Card>

          {/* Top Destinations Chart */}
          {bookingsData.length > 0 && (
            <Card className="border-slate-200 mt-6">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Top Destinations by Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bookingsData.slice(0, 10).sort((a, b) => (b.revenue || 0) - (a.revenue || 0))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="destination" stroke="#64748b" fontSize={11} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                      <Bar dataKey="revenue" fill="#383838" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
